import { getAuthToken } from '@/utils/authToken';
import { request } from '@/services/http';
import { designerTaskApiService } from '@/services/artApi';

export const DESIGNER_TASK_STATUS = {
  PENDING_ASSIGNMENT: 'PENDING_ASSIGNMENT',
  DESIGN_PENDING: 'DESIGN_PENDING',
  DESIGNING: 'DESIGNING',
  DESIGN_COMPLETED: 'DESIGN_COMPLETED',
  RETURNED_BY_FACTORY: 'RETURNED_BY_FACTORY',
  RETURNED_TO_SALES: 'RETURNED_TO_SALES',
  REFUNDED: 'REFUNDED',
  VOIDED: 'VOIDED',
};

export const designerTaskStatusMeta = {
  PENDING_ASSIGNMENT: { label: '待分配', tone: 'muted' },
  DESIGN_PENDING: { label: '待设计', tone: 'warning' },
  DESIGNING: { label: '设计中', tone: 'processing' },
  DESIGN_COMPLETED: { label: '设计完成', tone: 'success' },
  RETURNED_BY_FACTORY: { label: '工厂退回', tone: 'danger' },
  RETURNED_TO_SALES: { label: '退回销售', tone: 'muted' },
  REFUNDED: { label: '已退款', tone: 'muted' },
  VOIDED: { label: '已作废', tone: 'muted' },
};

export function canStartDesignerTask(status) {
  return [
    DESIGNER_TASK_STATUS.DESIGN_PENDING,
    DESIGNER_TASK_STATUS.DESIGNING,
    DESIGNER_TASK_STATUS.RETURNED_BY_FACTORY,
  ].includes(status);
}

export function canReturnDesignerTask(status) {
  return [
    DESIGNER_TASK_STATUS.PENDING_ASSIGNMENT,
    DESIGNER_TASK_STATUS.DESIGN_PENDING,
    DESIGNER_TASK_STATUS.DESIGNING,
    DESIGNER_TASK_STATUS.RETURNED_BY_FACTORY,
  ].includes(status);
}

function parseSkuCollection(value) {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== 'string') return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function firstNonEmpty(...values) {
  return values.find(value => value !== undefined && value !== null && String(value).trim() !== '');
}

export function normalizeTaskSkus(item = {}) {
  const source = [
    item.skuList,
    item.skus,
    item.skuInfos,
    item.orderSkuList,
    item.orderItemList,
    item.orderItems,
    item.items,
  ].map(parseSkuCollection).find(list => list.length) || [];
  const rows = source.length ? source : [{
    id: firstNonEmpty(item.skuId, item.orderItemId, item.itemId, 'default'),
    skuName: firstNonEmpty(item.skuName, item.productName, item.sku, item.productCategory),
    skuCode: firstNonEmpty(item.skuCode, item.skuNo, item.productCode),
    specification: firstNonEmpty(item.specification, item.skuSpec, item.spec, item.sku),
    quantity: firstNonEmpty(item.quantity, item.count, item.num, item.productCount),
    productCategory: item.productCategory,
    material: firstNonEmpty(item.material, item.fabric),
    color: firstNonEmpty(item.color, item.colorName),
    size: firstNonEmpty(item.size, item.dimension),
    remark: item.designRemark,
    imageUrl: firstNonEmpty(item.imageUrl, item.thumbUrl, item.picUrl),
  }];

  return rows.map((row, index) => {
    const sku = row?.skuInfo && typeof row.skuInfo === 'object'
      ? { ...row, ...row.skuInfo }
      : (row || {});
    const id = firstNonEmpty(sku.id, sku.skuId, sku.orderItemId, sku.itemId, sku.skuCode, sku.skuNo, index + 1);
    const skuId = String(firstNonEmpty(sku.skuId, id));
    const codeFallback = skuId === 'default' ? '' : skuId;
    return {
      ...sku,
      id: String(id),
      skuId,
      code: String(firstNonEmpty(sku.code, sku.skuCode, sku.skuNo, sku.productCode, codeFallback) ?? ''),
      name: String(firstNonEmpty(sku.skuName, sku.productName, sku.title, sku.name, `SKU ${index + 1}`)),
      specification: String(firstNonEmpty(sku.specification, sku.skuProperties, sku.skuSpec, sku.spec, sku.sku, '') || ''),
      quantity: firstNonEmpty(sku.quantity, sku.count, sku.num, sku.productCount) ?? '',
      pieces: firstNonEmpty(sku.pieces) ?? '',
      urgentMode: String(firstNonEmpty(sku.urgentMode, '') || ''),
      productCategory: String(firstNonEmpty(sku.productCategory, sku.productTypeName, item.productCategory, '') || ''),
      material: String(firstNonEmpty(sku.material, sku.fabric, '') || ''),
      color: String(firstNonEmpty(sku.color, sku.colorName, '') || ''),
      size: String(firstNonEmpty(sku.size, sku.dimension, '') || ''),
      remark: String(firstNonEmpty(sku.remark, sku.designRemark, item.designRemark, '') || ''),
      imageUrl: String(firstNonEmpty(sku.imageUrl, sku.thumbUrl, sku.picUrl, '') || ''),
    };
  });
}

export function normalizeDesignerTask(item = {}) {
  const productCategory = item.productType === 1
    ? '袋子'
    : item.productType === 2
      ? '吊牌系'
      : (item.productCategory || '-');
  return {
    ...item,
    id: item.id ?? item.taskId,
    taskId: item.taskId ?? item.id,
    orderId: String(item.orderId ?? item.orderNo ?? ''),
    orderNo: String(item.orderNo ?? item.orderId ?? ''),
    storeName: item.shopName || item.storeName || '未知店铺',
    productCategory,
    serviceName: item.userName || item.serviceName || '-',
    orderTime: item.createdTime || item.orderTime || '-',
    customerDisplay: item.customerWw || item.customerName || item.customerWangwang || '-',
    skus: normalizeTaskSkus({ ...item, productCategory }),
  };
}

export const designerTaskService = {
  hasRemoteAuth: () => Boolean(getAuthToken()),
  getTaskList: (params) => designerTaskApiService.getTaskList(params),
  getTaskCount: (params) => designerTaskApiService.getTaskCount(params),
  getOrderDetail: (orderId) => request(`/api/art/order/${orderId}`, { method: 'GET' }),
  startTask: (taskId) => designerTaskApiService.startTask(typeof taskId === 'object' ? taskId : { taskId }),
  submitTask: (taskId) => designerTaskApiService.submitTask(typeof taskId === 'object' ? taskId : { taskId }),
  uploadDesignFile: (orderId, file) => {
    const body = new FormData();
    body.append('file', file);
    return request(`/api/art/order/${orderId}/design/upload`, { method: 'POST', body });
  },
  claimTask: (count = 1) => designerTaskApiService.claimTask(typeof count === 'object' ? count : { count }),
  returnToSales: (payload) => designerTaskApiService.returnToSales(payload),
  downloadAttachment: (orderId) => request(`/api/art/order/${orderId}/attachment/zip`, { method: 'GET' }),
  /**
   * 下载任务素材（严格按后端顺序）：
   * 1) POST /api/art/designer/downloadAttachmentZip, { orderId: "11" }
   * 2) 仅待设计时 POST /api/art/designer/task/start?taskId=xx
   */
  downloadTaskMaterials: async ({ taskId, orderId, shouldStartTask = true } = {}) => {
    if (shouldStartTask && (taskId === undefined || taskId === null || String(taskId).trim() === '')) {
      throw new Error('缺少任务 taskId');
    }
    if (orderId === undefined || orderId === null || String(orderId).trim() === '') {
      throw new Error('缺少订单 orderId');
    }

    const payload = await designerTaskApiService.downloadAttachmentZip({ orderId: String(orderId) });
    if (shouldStartTask) {
      await designerTaskApiService.startTask({ taskId: String(taskId) });
    }
    return payload;
  },
  getConditions: (params) => designerTaskApiService.getConditions(params),
};
